import { Viewer, Worker } from "@react-pdf-viewer/core";
import { Button, Card, Checkbox, Form, Modal, notification, Popconfirm, Select, Tooltip, Upload } from 'antd';
import { useEffect, useState } from 'react';

// Plugins
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";

// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

import { DeleteTwoTone, InboxOutlined, PaperClipOutlined } from '@ant-design/icons';
import { useSaveDocumentGuide } from 'src/hooks/userGuides';
import service from "../../services/userGuideService";
import { programmes } from "../../utils/functions/apiConstants";
import { recordError } from "../../utils/logService";

import {
  ActivityIndicator,
  Messages,
  Notify,
  openNotification,
  pdfjsVersion,
  util
} from '@seims/shared';

const { Option } = Select;


const UserGuidesDetails = (props) => {

    const saveMutation = useSaveDocumentGuide();

    const {onShow, type: modalType, record: documentRecord} = props;
    

    const initWidth = 700;
    const initFileLabel = "Document File";
    const defaultLayoutPluginInstance = defaultLayoutPlugin();

    const [disabled, setDisabled] = useState(true);
    const [uploadedFile, setUploadedFile] = useState();
    const [docTitle, setDocTitle] = useState();
    const [docData, setDocData] = useState({});
    const [canUploadFile, setCanUploadFile] = useState(false);

    const [modalWidth, setModalWidth] = useState(initWidth);
    const [fileLabel, setFileLabel] = useState(initFileLabel);
    const [fileRequired, setFileRequired] = useState("add/Upload")


    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState("http://localhost");
    const [previewTitle, setPreviewTitle] = useState("");

    const [toolTipMessageSave, setToolTipMessageSave] = useState("Please complete the form");

    const [submitted, setSubmitted] = useState(false);
    const [progressMessage, setProgressMessage] = useState("");
    const [form] = Form.useForm();
    const formName = "UserGuide";
    const allowedFileSize = ((process.env.NODE_ENV === "development")
        ? process.env.REACT_DOCREPO_UPLOAD_LIMIT
        : window._CONFIGS.REACT_DOCREPO_UPLOAD_LIMIT) || 52428800;
    const fSize = allowedFileSize / 1024 / 1024;

    let msg = "";
    let heading = "";
    const { Dragger} = Upload;

    useEffect(() => {
        toggleModalTitles();
        setFileRequired("add/Upload");
        setFileLabel(modalType !== "view" ? initFileLabel : "Uploaded File");
        setCanUploadFile(modalType !== "view");
        setDocData(documentRecord);

        onFormLoad();
        window.addEventListener('resize', () => {
            const screenWidth = window.innerWidth;
            if (screenWidth < initWidth) {
                setModalWidth(screenWidth * 0.8);
            }
        })
    }, [documentRecord, window]);

    const toggleProgressMessage = (target, action) => {
        const _fMsg = `${target} is being ${action}, please wait...`
        setProgressMessage(_fMsg);
    }
    const confirmCancel = () => {
        setSubmitted(false);
        form.resetFields();
        onShow(false);
        setDisabled(true);
        setUploadedFile(null);
    };
    const handleCancel = () => {
        if (
            form.getFieldValue("uploadedFile") ||
            form.getFieldValue("targetModule") ||
            (uploadedFile !== null && uploadedFile?.length > 0)
        ) {
            setDisabled(false);
        } else {
            confirmCancel();
        }
    };
    const handleUpload = (file) => {
        const allowedFileTypes = ['application/pdf'];

        if (!allowedFileTypes.includes(file.type)) {
            openNotification("File Validation", `${file.name} is not a valid file type`, Notify.ERROR);
            return Upload.LIST_IGNORE;
        } else {
            setDisabled(false);
            setToolTipMessageSave("")
        }

        notification.destroy("seims");

        if (file.size > allowedFileSize) {
            openNotification("File Size", `File must be smaller than ${fSize}MB`, Notify.WARNING);

            return Upload.LIST_IGNORE;
        }
        setUploadedFile(file);
        return false;
    };
    const uploadInfo = {
        accept: ".pdf",
        multiple: false,
        showUploadList: false,
        beforeUpload: handleUpload,
        onRemove() {
            setUploadedFile(null);
        },
    };
    const toggleModalTitles = () => {
        var _title = modalType?.charAt(0).toUpperCase() + modalType?.slice(1);
        setDocTitle(_title + " User Guide");
    };

    const submitDocument = async (values) => {
        
        const formData = new FormData();
        formData.append("file", uploadedFile);

        const noFile = (uploadedFile === undefined || uploadedFile === null || uploadedFile.length === 0);
        if (fileRequired && noFile) {
            msg = util.parseMessage(Messages.FILE_UPLOAD_NO_FILE, "Documents");
            openNotification("File upload Failed", msg, Notify.ERROR);
            return;
        }
        const action = "uploaded";
        toggleProgressMessage("Document", action);

        const payload = {uuid: documentRecord?.uuid, archived: values?.archived, targetModule: values.targetModule, document: formData}

        setSubmitted(true);
        await saveMutation.mutateAsync(payload)
        .then((data) => {
            onShow(false);
            form.resetFields();
            setDisabled(true);
            setToolTipMessageSave("Please complete the form")
            return data;
        })
        .catch(() => {}) // Handled by mutation onError
        .finally(()=> {
                setSubmitted(false);
        });
    };


    const onFormLoad = () => {
        const data = {...documentRecord};

        if (!util.isEmpty(data)) {
            form.setFieldsValue({
              uuid: data?.uuid,
              targetModule: data?.targetModule,
              mediaId: data?.mediaId,
              title: data?.title,
              archived: data?.archived
            });
        }
    };


    const handlePreviewCancel = () => {
        setPreviewOpen(false);
    }


    const previewDocument = async (uuid, filename) => {
        try {
            toggleProgressMessage("File", "downloaded");
            setSubmitted(true);
            const response = await service.downloadFile(uuid);
            if (response && response.status === 200) {
                setSubmitted(false);
                const { data } = response;
                const new_blob = new Blob([data]);
                const url = URL.createObjectURL(new_blob);

                setPreviewImage(url);
                setPreviewOpen(true);
                setPreviewTitle("Preview: " + filename);
            }

        } catch (error) {
            recordError(error);
        }
    };


    return (
        <>
            <Modal
                className='doc-repo'
                key={"dd-main-modal"}
                title={docTitle}
                open={!!documentRecord}
                closable={false}
                keyboard={false}
                maskClosable={false}
                width={modalWidth}

                footer={[
                    <div className="modal-footer-buttons" key={"main-div-dd"}>
                        {modalType === "view" && (
                            <Button
                                onClick={() => confirmCancel()}
                                style={{ marginLeft: 10 }}
                            >
                                Okay
                            </Button>
                        )}
                        {(modalType === "add" || modalType === "edit") && (
                            canUploadFile && (

                                <Tooltip title={toolTipMessageSave} key="2tooltip">
                                    <Button
                                        disabled={disabled || saveMutation.isLoading}
                                        key="dd-btn-2"
                                        type="primary"
                                        htmlType="submit"
                                        form={formName}
                                        style={{ marginLeft: 10 }}
                                    >
                                        Save
                                    </Button>
                                </Tooltip>

                            )
                        )}

                        <Popconfirm
                            key="dd-pop-1"
                            title="Are you sure you want to cancel...you will lose all info entered?"
                            okText="Yes"
                            cancelText="No"
                            onConfirm={() => confirmCancel()}
                            onCancel={() => handleCancel()}
                            disabled={disabled}
                        >
                            {modalType !== "view" && (
                                <Button
                                    onMouseUp={() => handleCancel()}
                                    style={{ marginLeft: 10 }}
                                >
                                    Cancel
                                </Button>
                            )}

                        </Popconfirm>
                    </div >,
                ]}
            >
                <Form
                    key={"dd-main-form"}
                    disabled={modalType === "view"}
                    labelCol={{
                        span: 6,
                    }}
                    wrapperCol={{
                        span: 18,
                    }}
                    form={form}
                    id={formName}
                    name={formName}
                    onFinish={submitDocument}
                >
                    <Card>
                        <Form.Item
                            name="targetModule"
                            label="Target Module"
                            rules={[
                                {
                                    required: true,
                                    message: "Please select target module",
                                },
                            ]}
                        >
                            <Select
                                placeholder="Select a option and change input text above"
                                allowClear
                            >
                                {programmes.map((module) => {
                                    return <Option key={module.key} value={module.key}>{module.value}</Option>;
                                })}
                            </Select>
                        </Form.Item>
                        {canUploadFile && (
                            <div style={{ alignContent: 'left', marginLeft: "160px", color: "blue" }}><b>Max file size allowed : {fSize}MB and accepted file type : PDF</b></div>
                        )}
                        <Form.Item

                            name={"uploadedFile"}
                            label={fileLabel}
                            rules={[
                                {
                                    required: false,
                                    message: "Please select a file to upload"
                                }
                            ]}
                        >

                            {canUploadFile && (
                                <>
                                    <Dragger

                                        defaultFileList={[]}
                                        key={"up"}
                                        maxCount={1}
                                        multiple={false}
                                        {...uploadInfo}

                                    >
                                        <p className="ant-upload-drag-icon">
                                            <InboxOutlined />
                                        </p>
                                        Click or drag file to this area to upload
                                    </Dragger>
                                    <br></br>
                                    {uploadedFile?.name && (
                                        <>
                                            <span ><PaperClipOutlined /> Selected file: &nbsp; {uploadedFile?.name}</span>
                                            <span style={{ float: 'right' }} onClick={() => setUploadedFile(null)}><DeleteTwoTone style={{ color: 'red!important' }} />&nbsp; </span>
                                            <br />
                                        </>
                                    )}
                                </>
                            )}
                            {(docData?.uuid && docData?.filename) && (
                                <>
                                    {(modalType !== "view") && (
                                        <> &nbsp;Uploaded File: &nbsp;</>
                                    )}
                                    <a key={docData?.filename} onClick={() => previewDocument(docData?.uuid, docData?.filename)}>{docData?.filename}</a>
                                </>
                            )}
                        </Form.Item>

                        <Form.Item
                            name="archived"
                            label="Archived"
                            valuePropName="checked"
    
                        >
                            <Checkbox />
                        </Form.Item>
                        
                        {(submitted === true) && (
                            <>
                                <hr />
                                <ActivityIndicator visible={true} message={progressMessage} />
                            </>
                        )}
                    </Card>
                </Form>
                {
                    previewOpen && (
                        <Modal
                            key={"doc-modal-preview"}
                            open={previewOpen}
                            title={previewTitle}
                            footer={null}
                            onCancel={handlePreviewCancel}
                            onOk={handlePreviewCancel}
                            width={modalWidth}
                            maskClosable={false}
                            bodyStyle={{
                                height: "80vh",
                            }}
                        >
                            <Worker
                                workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsVersion()}/build/pdf.worker.min.js`}
                            >
                                <Viewer
                                    fileUrl={previewImage}
                                    plugins={[defaultLayoutPluginInstance]}
                                    renderError={console.log}
                                />
                            </Worker>
                        </Modal>
                    )
                }
            </Modal >
        </>
    )
}

export default UserGuidesDetails;
